import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Loader2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ROLES = ["super_admin", "hospital_admin", "faculty", "department_head", "student"];
const STATUSES = ["active", "inactive", "suspended", "pending_verification"];

function passwordRules(pw) {
  return {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function AdminUserFormPage() {
  const { userId } = useParams();
  const isEdit = Boolean(userId);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", mobile: "",
    role_code: "student", organization_id: "none", department_id: "none",
    designation: "", employee_id: "", account_status: "active",
    password: "", confirm: "",
  });
  // Optional "Also generate a paid invoice" block (student only, create flow).
  const [invoice, setInvoice] = useState({
    enabled: false, course_id: "", unit_price: 0,
    payment_method: "cash", payment_reference: "", send_email: true,
  });
  const [courses, setCourses] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/organizations");
      setOrgs(data.data.organizations);
    })();
  }, []);

  const loadDepts = useCallback(async (orgId) => {
    if (!orgId || orgId === "none") { setDepts([]); return; }
    const { data } = await api.get("/admin/departments", { params: { organization_id: orgId } });
    setDepts(data.data.departments);
  }, []);

  useEffect(() => { loadDepts(form.organization_id); }, [form.organization_id, loadDepts]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${userId}`);
        const u = data.data.user;
        setForm((f) => ({
          ...f,
          first_name: u.first_name || "",
          last_name: u.last_name || "",
          email: u.email || "",
          mobile: u.mobile || "",
          role_code: u.role_code || "student",
          organization_id: u.organization_id || "none",
          department_id: u.department_id || "none",
          designation: u.designation || "",
          employee_id: u.employee_id || "",
          account_status: u.account_status || "active",
        }));
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/app/super-admin/users");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, userId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isEdit) {
      if (form.password !== form.confirm) {
        setError("Passwords do not match.");
        return;
      }
      const rules = passwordRules(form.password);
      if (!Object.values(rules).every(Boolean)) {
        setError("Password must be 8+ chars with upper, lower, digit and special.");
        return;
      }
    }
    if (["hospital_admin", "department_head"].includes(form.role_code) && form.organization_id === "none") {
      setError("Organization is required for this role.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.confirm;
      if (payload.organization_id === "none") payload.organization_id = null;
      if (payload.department_id === "none") payload.department_id = null;
      if (isEdit) {
        delete payload.password;
        await api.put(`/admin/users/${userId}`, payload);
        toast.success("User updated.");
      } else {
        const { data: created } = await api.post("/admin/users", payload);
        toast.success("User created.");
        // If the admin chose to also raise a paid invoice, fire that now.
        const newUserId = created?.data?.id || created?.data?.user?.id || created?.id;
        if (invoice.enabled && newUserId && invoice.course_id && invoice.unit_price > 0) {
          try {
            await api.post(`/admin/students/${newUserId}/manual-invoice`, {
              course_id: invoice.course_id,
              unit_price: Number(invoice.unit_price),
              payment_method: invoice.payment_method,
              payment_reference: invoice.payment_reference,
              send_email: invoice.send_email,
            });
            toast.success("Manual invoice generated" + (invoice.send_email ? " and emailed." : "."));
          } catch (e2) { toast.error(formatApiError(e2)); }
        }
      }
      navigate("/app/super-admin/users");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title={isEdit ? "Edit user" : "Add user"}
      breadcrumbs={["Platform", "Users", isEdit ? "Edit" : "Add"]}
    >
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to users
      </button>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading…</div>
      ) : (
        <form onSubmit={submit} className="max-w-3xl space-y-6" data-testid={ADMIN.userFormRoot}>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#329112]">Identity</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="First name" required><Input required value={form.first_name} onChange={set("first_name")} data-testid={ADMIN.userFormFirst} /></Field>
              <Field label="Last name" required><Input required value={form.last_name} onChange={set("last_name")} data-testid={ADMIN.userFormLast} /></Field>
              <Field label="Email" required><Input type="email" required value={form.email} onChange={set("email")} data-testid={ADMIN.userFormEmail} /></Field>
              <Field label="Mobile" required><Input required value={form.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" data-testid={ADMIN.userFormMobile} /></Field>
              <Field label="Designation"><Input value={form.designation} onChange={set("designation")} data-testid={ADMIN.userFormDesignation} /></Field>
              <Field label="Employee ID"><Input value={form.employee_id} onChange={set("employee_id")} data-testid={ADMIN.userFormEmployeeId} /></Field>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#329112]">Role & Placement</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Role" required>
                <Select value={form.role_code} onValueChange={set("role_code")}>
                  <SelectTrigger data-testid={ADMIN.userFormRole}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Account status" required>
                <Select value={form.account_status} onValueChange={set("account_status")}>
                  <SelectTrigger data-testid={ADMIN.userFormStatus}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Organization">
                <Select value={form.organization_id} onValueChange={(v) => { setForm((f) => ({ ...f, organization_id: v, department_id: "none" })); }}>
                  <SelectTrigger data-testid={ADMIN.userFormOrg}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.organization_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Department">
                <Select value={form.department_id} onValueChange={set("department_id")} disabled={form.organization_id === "none"}>
                  <SelectTrigger data-testid={ADMIN.userFormDept}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          {!isEdit && form.role_code === "student" && (
            <section className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-6" data-testid="manual-invoice-section">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#329112]">Also generate a paid invoice</h3>
                  <p className="text-xs text-slate-600 mt-1">Recommended for offline enrolments (cash, Google Pay, bank transfer).</p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700">
                  <input type="checkbox" checked={invoice.enabled} onChange={(e) => { setInvoice((i) => ({ ...i, enabled: e.target.checked })); if (e.target.checked && courses.length === 0) { api.get("/admin/courses?status=published&limit=200").then(({data}) => setCourses(data.data?.courses || [])).catch(() => {}); } }} data-testid="manual-invoice-enable" />
                  Enable
                </label>
              </div>
              {invoice.enabled && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Field label="Course" required>
                    <select className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={invoice.course_id} onChange={(e) => setInvoice({ ...invoice, course_id: e.target.value })} data-testid="manual-invoice-course">
                      <option value="">— Select —</option>
                      {courses.map((c) => <option key={c.id} value={c.id}>{c.course_title}</option>)}
                    </select>
                  </Field>
                  <Field label="Amount (₹)" required><Input type="number" value={invoice.unit_price} onChange={(e) => setInvoice({ ...invoice, unit_price: e.target.value })} data-testid="manual-invoice-price" /></Field>
                  <Field label="Payment method" required>
                    <select className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={invoice.payment_method} onChange={(e) => setInvoice({ ...invoice, payment_method: e.target.value })} data-testid="manual-invoice-method">
                      <option value="cash">Cash</option>
                      <option value="google_pay">Google Pay</option>
                      <option value="phonepe">PhonePe</option>
                      <option value="paytm">Paytm</option>
                      <option value="upi">UPI (other)</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="card">Card</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Reference / UTR (optional)"><Input value={invoice.payment_reference} onChange={(e) => setInvoice({ ...invoice, payment_reference: e.target.value })} placeholder="e.g. UPI-TXN-1234567" data-testid="manual-invoice-ref" /></Field>
                  <label className="col-span-2 inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <input type="checkbox" checked={invoice.send_email} onChange={(e) => setInvoice({ ...invoice, send_email: e.target.checked })} data-testid="manual-invoice-email" />
                    Email the invoice PDF to the student
                  </label>
                </div>
              )}
            </section>
          )}

          {!isEdit && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#329112]">Initial password</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Password" required><Input type="password" required value={form.password} onChange={set("password")} data-testid={ADMIN.userFormPassword} /></Field>
                <Field label="Confirm password" required><Input type="password" required value={form.confirm} onChange={set("confirm")} data-testid={ADMIN.userFormConfirm} /></Field>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Must contain 8+ characters, one uppercase, one lowercase, one digit and one special character.
              </p>
            </section>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => navigate("/app/super-admin/users")}
                    className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid={ADMIN.userFormSubmit}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Create user")}
            </button>
          </div>
        </form>
      )}
    </DashboardLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
        {label}{required && <span className="ml-1 text-[#329112]">*</span>}
      </Label>
      {children}
    </div>
  );
}
